import { AxiosError } from 'axios';

const HTTP_CODE_400 = 400;
const HTTP_CODE_401 = 401;
const HTTP_CODE_403 = 403;
const HTTP_CODE_500 = 500;
const HTTP_CODE_540 = 540;

const ERROR_MESSAGE_API_CRSF_1: EappApiError = {
  httpCode: HTTP_CODE_403,
  message: 'The state modifying request has an invalid CSRF Token',
};
const ERROR_MESSAGE_API_CRSF_2: EappApiError = {
  httpCode: HTTP_CODE_403,
  message: 'The state modifying request is missing a CSRF Token',
};
const ERROR_MESSAGE_API_CORS_1: EappApiError = {
  httpCode: HTTP_CODE_400,
  message: 'CORS request denied'
};
const ERROR_MESSAGE_API_SESSION_1: EappApiError = {
  httpCode: HTTP_CODE_401,
  message: 'Unauthorized: Request is missing a session cookie',
};
const ERROR_MESSAGE_API_SESSION_2: EappApiError = {
  httpCode: HTTP_CODE_401,
  message: 'Auth failed because of an invalid session',
};
const ERROR_MESSAGE_API_SESSION_3: EappApiError = {
  httpCode: HTTP_CODE_401,
  message: 'Auth failed because of an expired session',
};
const ERROR_MESSAGE_API_SESSION_4: EappApiError = {
  httpCode: HTTP_CODE_500,
  message: 'Internal Server Error',
};
const ERROR_MESSAGE_API_SAVE_1: EappApiError = {
  httpCode: HTTP_CODE_403,
  message: 'The account is currently locked',
};
const ERROR_MESSAGE_API_SAVE_2: EappApiError = {
  httpCode: HTTP_CODE_400,
  message: 'Payload not provided',
};
const ERROR_MESSAGE_API_SAVE_3: EappApiError = {
  httpCode: HTTP_CODE_400,
  message: 'Failed to deserialize payload',
};
const ERROR_MESSAGE_API_SAVE_4: EappApiError = {
  httpCode: HTTP_CODE_400,
  message: 'Error converting payload into valid entity',
};
const ERROR_MESSAGE_API_SAVE_5: EappApiError = {
  httpCode: HTTP_CODE_400,
  message: 'Error converting payload into valid section',
};
const ERROR_MESSAGE_API_SAVE_6: EappApiError = {
  httpCode: HTTP_CODE_500,
  message: 'Error saving entity data',
};
const ERROR_MESSAGE_API_SAVE_7: EappApiError = {
  httpCode: HTTP_CODE_540,
  message: 'Error encountered an unrecoverable error during saving',
};

export interface EappError {
    code: number,
    httpCode?: number,
    message: string,
}
export interface EappApiError {
    httpCode: number,
    message: string,
    body?: string,
}

/**
 * Gets a friendly error code back from API or other Axios messages.
 * This currently includes all elements related to saving, CRSF, CORS, Session, and Saving related errors.
 * First three digits of the error code, may represent the HTTP error Code, the following two digits indicate the arbitrary sequencing of error codes.
 * @param code
 * @param err
 * @returns
 */
export const getErrorCode = (err: Error | AxiosError): EappError => {
  const axiosErr = err as AxiosError;
  if (axiosErr) {
    if (axiosErr.code === 'ECONNABORTED') {
      return { code: 40801, message: 'Abort/Timeout' };
    }
    if (axiosErr.code === 'ETIMEDOUT') {
      return { code: 40802, message: 'Timeout' };
    }
    const status = axiosErr.response?.status;
    if (!status) { // handles MESSAGE_API or misconfigured URLS
      if (axiosErr.code === 'ERR_BAD_OPTION_VALUE') {
        return { code: 40803, message: `Bad Option Value: ${axiosErr.message}` };
      }
      if (axiosErr.code === 'ERR_BAD_OPTION') {
        return { code: 40804, message: `Bad Option: ${axiosErr.message}` };
      }
      if (axiosErr.code === 'ERR_NETWORK') {
        return { code: 40805, message: `Network: ${axiosErr.message}` };
      }
      if (axiosErr.code === 'ERR_FR_TOO_MANY_REDIRECTS') {
        return { code: 40806, message: `To Many Redirects: ${axiosErr.message}` };
      }
      if (axiosErr.code === 'ERR_DEPRECATED') {
        return { code: 40807, message: `Deprecated: ${axiosErr.message}` };
      }
      if (axiosErr.code === 'ERR_BAD_RESPONSE') {
        return { code: 40808, message: `Bad Response: ${axiosErr.message}` };
      }
      if (axiosErr.code === 'ERR_BAD_REQUEST') {
        return { code: 40809, message: `Bad Request: ${axiosErr.message}` };
      }
      if (axiosErr.code === 'ERR_CANCELED') {
        return { code: 40810, message: `Canceled: ${axiosErr.message}` };
      }
      return { code: 40087, message: `Axios Code ${axiosErr.code}` };
    }
    const message: string | undefined = axiosErr.response?.data?.errors?.[0].message;
    switch (true) { // true makes this a searched case
      case ERROR_MESSAGE_API_CRSF_1.httpCode === status
                && ERROR_MESSAGE_API_CRSF_1.message.toLowerCase() === message?.toLowerCase():
        return { ...ERROR_MESSAGE_API_CRSF_1, code: 40301 };
      case ERROR_MESSAGE_API_CRSF_2.httpCode === status
                && ERROR_MESSAGE_API_CRSF_2.message.toLowerCase() === message?.toLowerCase():
        return { ...ERROR_MESSAGE_API_CRSF_2, code: 40302 };
      case ERROR_MESSAGE_API_CORS_1.httpCode === status
                && ERROR_MESSAGE_API_CORS_1.message.toLowerCase() === message?.toLowerCase():
        return { ...ERROR_MESSAGE_API_CORS_1, code: 40001 };
      case ERROR_MESSAGE_API_SESSION_1.httpCode === status
                && ERROR_MESSAGE_API_SESSION_1.message.toLowerCase() === message?.toLowerCase():
        return { ...ERROR_MESSAGE_API_SESSION_1, code: 40101 };
      case ERROR_MESSAGE_API_SESSION_2.httpCode === status
                && ERROR_MESSAGE_API_SESSION_2.message.toLowerCase() === message?.toLowerCase():
        return { ...ERROR_MESSAGE_API_SESSION_2, code: 40102 };
      case ERROR_MESSAGE_API_SESSION_3.httpCode === status
                && ERROR_MESSAGE_API_SESSION_3.message.toLowerCase() === message?.toLowerCase():
        return { ...ERROR_MESSAGE_API_SESSION_3, code: 40103 };
      case ERROR_MESSAGE_API_SESSION_4.httpCode === status
                && ERROR_MESSAGE_API_SESSION_4.message.toLowerCase() === message?.toLowerCase():
        return { ...ERROR_MESSAGE_API_SESSION_4, code: 50001 };
      case ERROR_MESSAGE_API_SAVE_1.httpCode === status
                && ERROR_MESSAGE_API_SAVE_1.message.toLowerCase() === message?.toLowerCase():
        return { ...ERROR_MESSAGE_API_SAVE_1, code: 40303 };
      case ERROR_MESSAGE_API_SAVE_2.httpCode === status
                && ERROR_MESSAGE_API_SAVE_2.message.toLowerCase() === message?.toLowerCase():
        return { ...ERROR_MESSAGE_API_SAVE_2, code: 40002 };
      case ERROR_MESSAGE_API_SAVE_3.httpCode === status
                && ERROR_MESSAGE_API_SAVE_3.message.toLowerCase() === message?.toLowerCase():
        return { ...ERROR_MESSAGE_API_SAVE_3, code: 40003 };
      case ERROR_MESSAGE_API_SAVE_4.httpCode === status
                && ERROR_MESSAGE_API_SAVE_4.message.toLowerCase() === message?.toLowerCase():
        return { ...ERROR_MESSAGE_API_SAVE_4, code: 40004 };
      case ERROR_MESSAGE_API_SAVE_5.httpCode === status
                && ERROR_MESSAGE_API_SAVE_5.message.toLowerCase() === message?.toLowerCase():
        return { ...ERROR_MESSAGE_API_SAVE_5, code: 40005 };
      case ERROR_MESSAGE_API_SAVE_6.httpCode === status
                && ERROR_MESSAGE_API_SAVE_6.message.toLowerCase() === message?.toLowerCase():
        return { ...ERROR_MESSAGE_API_SAVE_6, code: 50002 };
      case ERROR_MESSAGE_API_SAVE_7.httpCode === status
                && ERROR_MESSAGE_API_SAVE_7.message.toLowerCase() === message?.toLowerCase():
        return { ...ERROR_MESSAGE_API_SAVE_7, code: 54002 };
      default:
        break;
    }
    return { code: (status * 1000) + 86, message: `Unknown Communications Error` }; // not a known error Possibly ISP, Load abalancer, firewall or other unknown error
  }
  return { code: 99986, message: `Unknown Error` }; // not related to axios communications
};
